"use client";

import ParagraphIcon from "@/components/paragraph-icon";
import { TVOverlay } from "@/components/tv-overlay";
import ProfileSetupDialog from "@/components/profile-setup-dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCategorizedApps, getGameApps, NAV_BEAR_IMAGES, APP_CHARACTER_IMAGES } from "@/constants/apps";
import {
  ARCADE_STYLES,
  HIBERNATION_POKU_FILTER,
  HIBERNATION_POKU_FILTER_LIVING,
  HIBERNATION_POKU_FILTER_STRONG,
  KANSEI_DEAD,
} from "@/constants/arcade-styles";
import { PRIMARY_CHAIN_ID } from "@/constants/chain";
import { BRAND_COLORS } from "@/constants/colors";
import { DEFAULT_PROFILE_BACKGROUND, DEFAULT_PROFILE_CHARACTER } from "@/constants/profile";
import { useLogin } from "@/hooks/auth";
import { usePortfolioBalances } from "@/hooks/data/use-portfolio";
import { useAppAvatarParts, useCurrentProfile, useProfileSettings } from "@/hooks/profile";
import { useTokenPrice } from "@/hooks/use-token-price";
import { useProfileSetupPrompt } from "@/store/ui/profile-setup-prompt";
import { cn, shortenAddress } from "@/utils";
import * as Dialog from "@radix-ui/react-dialog";
import { useSoundtrackDirector } from "@thehoneyjar/sound-engine";
import { Lock, LogOut, Menu, Pause, Play, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

// Use the optimized, cropped THJ SVG asset from public/
const NAVBAR_EQUALIZER_HEIGHTS = [0.3, 0.7, 0.4, 0.9, 0.5];

const Navbar = () => {
  const _pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileTab, setProfileTab] = useState("tokens");
  const [profileSetupOpen, setProfileSetupOpen] = useState(false);
  const { hasPrompted, markPrompted } = useProfileSetupPrompt();
  const { profile } = useCurrentProfile();
  const { wallets, login, logout, address, user, sdkHasLoaded } = useLogin();
  const _evmAddress = (wallets.EVM?.address || "") as `0x${string}` | undefined;
  const { isLoading: profileSettingsLoading } = useProfileSettings();
  const { character, background } = useAppAvatarParts();
  const {
    status: soundtrackStatus,
    masterEnabled,
    musicEnabled,
    effectsEnabled,
    setMasterEnabled,
    setMusicEnabled,
    setEffectsEnabled,
    play: playSoundtrack,
    pause: pauseSoundtrack,
  } = useSoundtrackDirector();
  const displayName =
    profile?.username ||
    profile?.name ||
    (wallets.EVM?.address ? shortenAddress(wallets.EVM.address) : "");
  const displayAddress = wallets.EVM?.address ? shortenAddress(wallets.EVM.address) : "";
  // Portfolio balances & USD
  const { sortedHoldings, totalUSD, henloAmount, beraAmount } = usePortfolioBalances({
    address: (wallets.EVM?.address || "") as `0x${string}`,
    chainId: PRIMARY_CHAIN_ID,
    includeGauges: true,
  });

  // Get HENLO price
  const { data: priceData } = useTokenPrice();
  // Always show two decimals on right-hand USD column
  const fmtUSD2 = (v?: number) => (typeof v === "number" ? `$${(v || 0).toFixed(2)}` : "$0.00");
  const fmtUSDWhole = (v: number) =>
    `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtToken = (v: number) => {
    if (v === 0) return "0.00";
    if (v < 0.0001) return v.toFixed(6);
    if (v < 1) return v.toFixed(4);
    if (v < 1000) return v.toFixed(2);
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const netValueUSD = totalUSD;

  // Analytics removed

  // Auto-open profile setup only when:
  // - Dynamic SDK has loaded and user is authenticated
  // - Profile row has been created
  // - Profile settings have loaded (to avoid race where settings are undefined temporarily)
  // - Username or avatar parts are missing
  useEffect(() => {
    if (!sdkHasLoaded) return; // wait for Dynamic state
    const isLoggedIn = Boolean(user?.userId);
    if (!isLoggedIn) return; // never open when not logged in
    if (!profile?.id) return; // wait for profile creation
    if (profileSettingsLoading) return; // wait until settings fetch resolves

    const needsUsername = !profile?.username;
    const needsAvatar = !(character && background);
    const alreadyPrompted = hasPrompted(user?.userId);
    if ((needsUsername || needsAvatar) && !alreadyPrompted) {
      setProfileSetupOpen(true);
      // Mark immediately to avoid re-opening across tabs/reloads
      markPrompted(user?.userId);
    }
  }, [
    sdkHasLoaded,
    user?.userId,
    profile?.id,
    profile?.username,
    character,
    background,
    profileSettingsLoading,
    hasPrompted,
    markPrompted,
  ]);

  // Close dialog if user logs out or profile disappears
  useEffect(() => {
    const isLoggedIn = Boolean(user?.userId);
    if (!isLoggedIn || !profile?.id) {
      if (profileSetupOpen) setProfileSetupOpen(false);
    }
  }, [user?.userId, profile?.id, profileSetupOpen]);

  // Dev-only drawer mock: visiting `?drawer=open` in development pops the
  // Profile Drawer regardless of auth state so chrome iteration (feel-iterate)
  // can run without a Dynamic login. Gated on NODE_ENV at build time so the
  // param is inert in production.
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    if (searchParams?.get("drawer") === "open") setProfileDrawerOpen(true);
  }, [searchParams]);

  // Analytics removed

  // Navigation structure sourced from unified catalog.
  // Hibernation: EARN (vault, lock) and GUTS (incinerator, wall) are
  // permanently sunset — Foundation reward realignment killed BGT and the
  // surfaces have no path back. Routes still resolve for direct links, but
  // the navbar no longer surfaces them.
  const navApps = getGameApps("", "");
  const categorized = getCategorizedApps(navApps);
  // AFTER HOURS nav: desktop fans the individual apps out as direct
  // character links — no category groupings, no dropdown. Each app
  // wears the character bear it had inside the old dropdown.
  // Operator (session-3): "Show each of the mememaker, incinerator,
  // and gallery as options instead of play/create and use the
  // characters they were associated with in the dropdown."
  const navigationItems = [
    {
      id: "mememaker",
      title: "MEME MAKER",
      bearImage: APP_CHARACTER_IMAGES.mememaker,
      path: "/mememaker",
      isLocked: false,
    },
    {
      id: "incinerator",
      title: "INCINERATOR",
      bearImage: APP_CHARACTER_IMAGES.incinerator,
      path: "/incinerator",
      isLocked: false,
    },
    {
      id: "gallery",
      title: "GALLERY",
      bearImage: APP_CHARACTER_IMAGES.gallery,
      path: "/gallery",
      isLocked: true,
    },
  ];

  // Mobile still uses the categorized items list since the slide-out
  // drawer has room for it. Compose from the same source so locked
  // state stays in sync.
  const navigationCategories = [
    {
      id: "play",
      title: "PLAY",
      bearImage: NAV_BEAR_IMAGES.play,
      items: categorized.play,
    },
    {
      id: "create",
      title: "CREATE",
      bearImage: NAV_BEAR_IMAGES.create,
      items: categorized.create,
    },
  ];

  const nowPlayingTitle = soundtrackStatus.title ?? "No Track";
  const isTrackPlaying = soundtrackStatus.isPlaying;

  const handleGlobalPlayPause = async () => {
    if (isTrackPlaying) {
      await pauseSoundtrack(true);
      return;
    }
    if (!masterEnabled || !musicEnabled) {
      // Gates closed → flip them; the autoplay effect detects the state
      // change and calls playMusic. Adding playSoundtrack here would
      // race with autoplay and double-fire the track.
      setMasterEnabled(true);
      setMusicEnabled(true);
    } else {
      // Gates already open (default localStorage state, or post-pause).
      // The autoplay effect won't re-fire on no-op state, so call
      // playSoundtrack directly. Mirrors use-app-music.ts re-enable path.
      await playSoundtrack({ fadeIn: true });
    }
  };

  // Map specific app items to custom character images for the navbar
  const getItemCharacter = (id: string, fallback: string) => {
    return APP_CHARACTER_IMAGES[id] || fallback;
  };

  const handleItemClick = (path: string, isLocked?: boolean) => {
    if (!isLocked && path !== "#") {
      router.push(path);
    }
  };

  return (
    <div className="relative z-50 w-full">
      <div className="fixed inset-x-0 top-0 z-50">
        <div
          className="relative h-32 w-full overflow-hidden"
          style={{
            // Hibernation marquee: deep cobalt chassis, unlit. Moonlight
            // wash removed — the strip no longer projects atmosphere; CRT
            // residue overlay below carries the powered-down register.
            background:
              "linear-gradient(180deg, #1c2a78 0%, #1a276e 50%, #15205c 100%)",
            boxShadow: `
              0 8px 24px rgba(0, 0, 0, 0.45),
              0 4px 12px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.04),
              inset 0 -1px 0 rgba(0, 0, 0, 0.55)
            `.trim(),
            borderBottom: "1px solid rgba(62, 97, 250, 0.25)",
          }}
        >
          {/* Lock texture overlay - on the grey bottom bar */}
          <div
            className="pointer-events-none absolute top-0 z-10 h-14 w-full"
            style={{
              backgroundImage: "url('/assets/lock_texture.png')",
              backgroundRepeat: "repeat",
              backgroundSize: "100px",
              mixBlendMode: "overlay",
              opacity: 0.5,
            }}
          ></div>

          {/* CRT scanline residue — KANSEI_DEAD register. Replaces the
              former moonlight halo; the chassis reads as a powered-down
              CRT, not an atmospheric room. */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: KANSEI_DEAD.SCANLINE_BG_2,
              opacity: 0.6,
              mixBlendMode: "screen",
            }}
          ></div>

          {/* Inner bezel frame */}
          <div
            className="pointer-events-none absolute inset-[2px]"
            style={{
              background:
                "linear-gradient(145deg, rgba(0, 0, 0, 0.2) 0%, transparent 50%, rgba(120, 150, 255, 0.04) 100%)",
              boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.55)",
            }}
          ></div>

          <div
            className="absolute bottom-8 h-10 w-full"
            style={{
              // Bezel bar — lean to the same dark cobalt-rimmed vocabulary
              // as ARCADE_STYLES.TV_FRAME so the navbar reads as the same
              // chassis as the lobby cabinet.
              background:
                "linear-gradient(145deg, #2a2a32 0%, #1f1f25 30%, #161620 70%, #0d0d12 100%)",
              boxShadow: `
              0 -4px 12px rgba(0, 0, 0, 0.5),
              0 4px 12px rgba(0, 0, 0, 0.55),
                inset 0 1px 0 rgba(255, 255, 255, 0.04),
                inset 0 -1px 0 rgba(0, 0, 0, 0.7),
                0 0 0 1px rgba(62, 97, 250, 0.18)
              `.trim(),
              borderTop: "1px solid rgba(62, 97, 250, 0.22)",
            }}
          >
            {/* Outer highlight for raised effect */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(135deg, rgba(120, 150, 255, 0.05) 0%, transparent 40%)",
                opacity: 0.8,
              }}
            ></div>
            {/* Inner bezel frame */}
            <div
              className="pointer-events-none absolute inset-[2px]"
              style={{
                background:
                  "linear-gradient(145deg, rgba(0, 0, 0, 0.3) 0%, transparent 50%, rgba(120, 150, 255, 0.04) 100%)",
                boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.55)",
              }}
            ></div>
          </div>

          {/* New Sub-Navigation Bar — same cobalt rim as TICKER_CONTAINER */}
          <div
            className="absolute bottom-0 h-8 w-full"
            style={{
              background: "linear-gradient(135deg, #08080a 0%, #11111a 100%)",
              boxShadow: `
                0 1px 4px rgba(0, 0, 0, 0.25),
                inset 0 1px 0 rgba(255, 255, 255, 0.04),
                inset 0 -1px 0 rgba(62, 97, 250, 0.18),
                0 0 0 1px rgba(62, 97, 250, 0.14)
              `.trim(),
              borderTop: "1px solid rgba(62, 97, 250, 0.18)",
            }}
          >
            <div className="pointer-events-auto relative z-30 mx-auto flex h-full max-w-[112rem] items-center justify-between px-6">
              {/* Profile Picture — powered-down CRT mounted on the
                  navbar chassis. The screen reads as off (deep moondust
                  + KANSEI scanline residue + hibernation filter on the
                  backdrop) but the user's character broadcasts through
                  it on the LIVING flicker — alive within the closed
                  room. Cobalt rim ties it to the marquee's chrome
                  vocabulary; no white halos, no daylight gradients. The
                  tiny cobalt LED at the corner is the "signal alive"
                  glyph — same flicker timing as the music-player album
                  art and the navbar's "Now Playing" indicator. */}
              {user && (
                <button
                  onClick={() => setProfileDrawerOpen(true)}
                  className="absolute right-8 -top-10 size-16 overflow-visible group"
                  style={{
                    // Hibernation moondust chassis — same material as
                    // the Burn/Build buttons + Settings/Close cluster +
                    // BYE MOONDUST button. The console deck repeats.
                    background: "linear-gradient(135deg, #1f1f25 0%, #161620 100%)",
                    boxShadow: `
                      0 4px 8px rgba(0, 0, 0, 0.55),
                      0 2px 4px rgba(0, 0, 0, 0.4),
                      inset 0 1px 0 rgba(255, 255, 255, 0.04),
                      inset 0 -1px 0 rgba(62, 97, 250, 0.18),
                      0 0 0 1px rgba(62, 97, 250, 0.22)
                    `.trim(),
                    transform: "translateZ(0)",
                    willChange: "transform",
                    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                  onMouseEnter={(e) => {
                    // Hover — cobalt rim brightens to KANSEI_DEAD.GLOW.DIM
                    // tier. No lift (AFTER HOURS surfaces don't bounce);
                    // a 1.01 scale registers the click target without
                    // breaking the hibernation register.
                    e.currentTarget.style.transform = "scale(1.01) translateZ(0)";
                    e.currentTarget.style.boxShadow = `
                      0 4px 12px rgba(0, 0, 0, 0.6),
                      0 2px 6px rgba(0, 0, 0, 0.45),
                      inset 0 1px 0 rgba(255, 255, 255, 0.05),
                      inset 0 -1px 0 rgba(62, 97, 250, 0.32),
                      0 0 0 1px rgba(62, 97, 250, 0.4),
                      0 0 14px rgba(62, 97, 250, 0.22)
                    `.trim();
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateZ(0)";
                    e.currentTarget.style.boxShadow = `
                      0 4px 8px rgba(0, 0, 0, 0.55),
                      0 2px 4px rgba(0, 0, 0, 0.4),
                      inset 0 1px 0 rgba(255, 255, 255, 0.04),
                      inset 0 -1px 0 rgba(62, 97, 250, 0.18),
                      0 0 0 1px rgba(62, 97, 250, 0.22)
                    `.trim();
                  }}
                  onMouseDown={(e) => {
                    // Press — light depress. Stepped easing implied by
                    // the short transition; the deck reads as physical.
                    e.currentTarget.style.transform = "scale(0.98) translateZ(0)";
                    e.currentTarget.style.boxShadow = `
                      0 2px 4px rgba(0, 0, 0, 0.5),
                      0 1px 2px rgba(0, 0, 0, 0.35),
                      inset 0 2px 3px rgba(0, 0, 0, 0.4),
                      inset 0 -1px 0 rgba(62, 97, 250, 0.14),
                      0 0 0 1px rgba(62, 97, 250, 0.22)
                    `.trim();
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "scale(1.01) translateZ(0)";
                    e.currentTarget.style.boxShadow = `
                      0 4px 12px rgba(0, 0, 0, 0.6),
                      0 2px 6px rgba(0, 0, 0, 0.45),
                      inset 0 1px 0 rgba(255, 255, 255, 0.05),
                      inset 0 -1px 0 rgba(62, 97, 250, 0.32),
                      0 0 0 1px rgba(62, 97, 250, 0.4),
                      0 0 14px rgba(62, 97, 250, 0.22)
                    `.trim();
                  }}
                >
                  {/* Monitor bezel — cobalt rim institutional, replaces
                      the generic black border. Same edge language as
                      the navbar marquee chassis. */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      border: "2px solid rgba(62, 97, 250, 0.18)",
                      boxShadow: `
                        inset 0 1px 0 rgba(255, 255, 255, 0.04),
                        inset 0 -1px 0 rgba(0, 0, 0, 0.55)
                      `.trim(),
                    }}
                  />

                  {/* Inner bezel step — recessed screen bed, deeper
                      black with a cobalt inner edge so the screen reads
                      as set INTO the chassis, not floating. */}
                  <div
                    className="absolute inset-[2px] pointer-events-none"
                    style={{
                      background: "#050507",
                      boxShadow: `
                        inset 0 2px 4px rgba(0, 0, 0, 0.92),
                        inset 0 -1px 0 rgba(62, 97, 250, 0.16),
                        inset 1px 0 0 rgba(62, 97, 250, 0.10),
                        inset -1px 0 0 rgba(62, 97, 250, 0.10)
                      `.trim(),
                    }}
                  />

                  {/* Screen display — powered-down CRT showing the user
                      through the residue. Background filter is heavy
                      (HIBERNATION_POKU_FILTER, "lights down" tier) so
                      the room reads as asleep. Character carries the
                      LIVING register so the user broadcasts through. */}
                  <div
                    className="absolute inset-[4px] overflow-hidden bg-black"
                    style={{
                      boxShadow: `
                        inset 0 1px 2px rgba(0, 0, 0, 0.85),
                        0 0 0 1px rgba(0, 0, 0, 0.9)
                      `.trim(),
                    }}
                  >
                    {/* background — room asleep */}
                    <Image
                      src={background || DEFAULT_PROFILE_BACKGROUND}
                      alt="bg"
                      fill
                      className="object-cover"
                      style={{
                        filter: HIBERNATION_POKU_FILTER,
                      }}
                    />
                    {/* character — alive within the hibernating room.
                        LIVING flicker matches the music-player album
                        art and the drawer's avatar treatment. */}
                    <Image
                      src={character || DEFAULT_PROFILE_CHARACTER}
                      alt="char"
                      fill
                      className="object-contain scale-110 kansei-flicker-living-c"
                      style={{
                        filter: HIBERNATION_POKU_FILTER_LIVING,
                      }}
                    />
                    {/* CRT scanline residue — KANSEI_DEAD register,
                        same powered-down CRT reading as the navbar
                        chassis + drawer header strip. */}
                    <div
                      className="pointer-events-none absolute inset-0"
                      style={{
                        backgroundImage: KANSEI_DEAD.SCANLINE_BG_2,
                        opacity: 0.55,
                        mixBlendMode: "screen",
                      }}
                    />
                    {/* Screen glass reflection — cobalt-tinted highlight
                        replaces the warm white. Subtle. */}
                    <div
                      className="pointer-events-none absolute inset-0 z-10"
                      style={{
                        background: `
                          linear-gradient(135deg,
                            rgba(94, 123, 250, 0.06) 0%,
                            transparent 30%,
                            transparent 70%,
                            rgba(94, 123, 250, 0.02) 100%)
                        `.trim(),
                      }}
                    />
                  </div>

                  {/* Hover halo — cobalt accent ring layered ON the
                      bezel. Stays cobalt across all states (no white
                      drop-shadow per AFTER HOURS register). */}
                  <div
                    className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100"
                    style={{
                      boxShadow: `
                        0 0 0 1px rgba(62, 97, 250, 0.35),
                        0 6px 14px rgba(62, 97, 250, 0.14),
                        inset 0 0 0 1px rgba(62, 97, 250, 0.12)
                      `.trim(),
                      transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                </button>
              )}
              {/* Mobile: hamburger */}
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open menu"
                  aria-expanded={mobileMenuOpen}
                  className="group flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium text-white/60 transition-colors hover:text-white"
                >
                  <Menu className="size-4 transition-transform group-hover:scale-110" />
                  <span>Menu</span>
                </button>
              </div>

              {/* Left side - AFTER HOURS screen with astronaut peeking
                  half-in / half-out at the left edge. The screen has
                  extra left padding so the bear has room to sit inside;
                  the bear is absolute-positioned + translated -50% on x
                  so half overlaps into the chrome. */}
              <div className="relative hidden md:flex items-center">
                <div
                  className="relative overflow-hidden rounded-[3px] py-1 pl-9 pr-3"
                  style={ARCADE_STYLES.TICKER_CONTAINER}
                >
                  <TVOverlay intensity="light" />
                  <p
                    className="kansei-flicker-living-c relative z-10 font-pressStart2P text-[7px] uppercase tracking-[0.22em]"
                    style={{
                      color: "rgba(155, 176, 240, 0.85)",
                      textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)",
                    }}
                  >
                    AFTER HOURS
                  </p>
                </div>

                {/* Astronaut — sits half-in / half-out at the screen's
                    left edge. Same character throughline as the
                    DisabledCabinet astronaut. LIVING filter since the
                    sign behind him is on. */}
                <Image
                  src="/poku/astronaut.png"
                  alt=""
                  width={28}
                  height={28}
                  className="pointer-events-none absolute left-0 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2"
                  style={{ filter: HIBERNATION_POKU_FILTER_LIVING }}
                />
              </div>

              {/* Right side - Navigation Links, Social, and Auth */}
              <div className={cn("flex items-center gap-2 mr-24", user ? "mr-24" : "mr-0")}>
                  {/* Soundtrack Controls - Premium Broadcast Control Panel */}
                <div
                  className="hidden md:flex relative items-center gap-1.5 px-3 pr-8 py-0.5 rounded-[3px] overflow-hidden w-[220px]"
                  style={{
                    // Recessed TV screen aesthetic with 8-layer shadow system
                    background: "linear-gradient(135deg, rgba(10,10,11,0.95) 0%, rgba(20,20,22,0.9) 100%)",
                    boxShadow: `
                      inset 0 2px 4px rgba(0,0,0,0.8),
                      inset 0 1px 2px rgba(0,0,0,0.6),
                      inset 0 4px 8px rgba(0,0,0,0.4),
                      inset 0 -1px 1px rgba(0,0,0,0.5),
                      inset 0 0 0 1px rgba(0,0,0,0.3),
                      0 1px 0 rgba(255,255,255,0.03),
                      0 0 12px rgba(255,255,255,0.03),
                      inset 0 0 20px rgba(255,255,255,0.01)
                    `.trim(),
                    border: "1px solid rgba(0,0,0,0.5)",
                    position: "relative"
                  }}
                >
                  {/* CRT Screen Overlay Effect */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `repeating-linear-gradient(
                        0deg,
                        transparent,
                        transparent 2px,
                        rgba(255, 255, 255, 0.01) 2px,
                        rgba(255, 255, 255, 0.01) 3px
                      )`,
                      backgroundSize: "100% 3px",
                      mixBlendMode: "screen",
                      opacity: 0.5
                    }}
                  />

                  {/* Vignette for depth */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.3) 100%)"
                    }}
                  />

                  {/* Play/Pause Button — cobalt-tinted moondust, no white
                      drop-shadow halo. AFTER HOURS register. */}
                  <button
                    className="relative group flex items-center justify-center p-0.5 transition-colors duration-200 active:scale-95"
                    onClick={() => void handleGlobalPlayPause()}
                    aria-label={isTrackPlaying ? "Pause music" : "Play music"}
                    style={{
                      color: isTrackPlaying
                        ? "rgba(200, 215, 255, 1)"
                        : "rgba(155, 176, 240, 0.7)",
                      transform: "translateZ(0)",
                    }}
                  >
                    <div className="relative">
                      {isTrackPlaying ? (
                        <Pause className="size-3.5 transition-transform duration-200 group-hover:scale-110" />
                      ) : (
                        <Play className="size-3.5 transition-transform duration-200 group-hover:scale-110" />
                      )}
                    </div>
                  </button>

                  {/* Visual Separator — cobalt hairline */}
                  <div
                    className="w-px h-4 relative"
                    style={{
                      background:
                        "linear-gradient(to bottom, transparent, rgba(62, 97, 250, 0.28), transparent)",
                    }}
                  />

                  {/* Track Info Display — moondust zinc, LIVING register
                      flicker when broadcasting (matches the lobby music
                      player's track info treatment). */}
                  <div className="relative flex flex-col leading-tight">
                    {/* Status Label */}
                    <span
                      className={`text-[6px] font-pressStart2P uppercase ${isTrackPlaying ? "kansei-flicker-living-c" : ""}`}
                      style={{
                        color: isTrackPlaying
                          ? "rgba(155, 176, 240, 0.75)"
                          : "rgba(155, 176, 240, 0.35)",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {isTrackPlaying ? "Now Playing" : "Paused"}
                    </span>

                    {/* Track Title with Marquee Effect for Long Titles */}
                    <div
                      className={`relative text-[8px] font-pressStart2P font-semibold uppercase overflow-hidden ${isTrackPlaying ? "kansei-flicker-living-b" : ""}`}
                      style={{
                        width: "90px",
                        color: isTrackPlaying
                          ? "rgba(200, 215, 255, 0.95)"
                          : "rgba(155, 176, 240, 0.5)",
                        textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)",
                      }}
                    >
                      {nowPlayingTitle.length > 11 && isTrackPlaying ? (
                        <div className="relative h-[10px]">
                          <div
                            className="absolute whitespace-nowrap"
                            style={{
                              animation: isTrackPlaying ? "marqueeScroll 8s linear infinite" : "none",
                              display: "inline-block",
                              paddingLeft: 0
                            }}
                          >
                            <span>{nowPlayingTitle}</span>
                            <span className="px-4">  </span>
                            <span>{nowPlayingTitle}</span>
                            <span className="px-4">  </span>
                          </div>
                        </div>
                      ) : (
                        <span className="block truncate">{nowPlayingTitle}</span>
                      )}
                    </div>
                  </div>

                  {/* Animated Equalizer Bars — cobalt-tinted moondust
                      gradient, no white drop-shadow halo. */}
                  <div
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-end gap-[3px] h-4 transition-opacity duration-300"
                    style={{
                      opacity: isTrackPlaying ? 1 : 0.2,
                    }}
                  >
                    {NAVBAR_EQUALIZER_HEIGHTS.map((height, i) => (
                      <div
                        key={i}
                        className={`w-0.5 origin-bottom ${isTrackPlaying ? "animate-equalizer-bar" : ""}`}
                        style={{
                          background: isTrackPlaying
                            ? "linear-gradient(to top, rgba(155, 176, 240, 0.85), rgba(155, 176, 240, 0.2))"
                            : "linear-gradient(to top, rgba(155, 176, 240, 0.3), rgba(155, 176, 240, 0.1))",
                          height: `${height * 14}px`,
                          animationName: isTrackPlaying ? "equalizerBar" : "none",
                          animationDuration: `${0.6 + i * 0.15}s`,
                          animationTimingFunction: "ease-in-out",
                          animationDelay: `${i * 0.05}s`,
                          animationIterationCount: isTrackPlaying ? "infinite" : "1",
                          animationPlayState: isTrackPlaying ? "running" : "paused",
                          transformOrigin: "bottom",
                          transition: "background 0.3s ease",
                        }}
                      />
                    ))}
                  </div>
                </div>

                                <div className="hidden md:block mx-1 h-4 w-px" style={{ background: "rgba(62, 97, 250, 0.22)" }}></div>


                {/* Social Links Group — cobalt-tinted moondust (was bright
                    white). Each icon dims/brightens on hover via the
                    same opacity gradient as the rest of the AFTER HOURS
                    chrome. Telegram is a flat-color SVG so it gets a
                    filter chain; paragraph/discord use currentColor and
                    inherit from a colored wrapper. */}
                <a
                  href="https://t.me/henlotelegram"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:flex group items-center justify-center rounded p-1"
                >
                  <Image
                    src="/social/telegram.svg"
                    alt="telegram"
                    width={14}
                    height={14}
                    className="size-3.5 opacity-55 transition-opacity group-hover:opacity-90"
                    style={{
                      // Tints the black-fill SVG to the cobalt-moondust
                      // band (~rgba(155, 176, 240)).
                      filter:
                        "brightness(0) saturate(100%) invert(72%) sepia(20%) saturate(900%) hue-rotate(195deg) brightness(0.95)",
                    }}
                  />
                </a>
                <a
                  href="https://paragraph.xyz/@henlo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:flex group items-center justify-center rounded p-1"
                >
                  {/* ParagraphIcon is a base64 PNG inside an <svg><image>
                      tag — currentColor does not tint it. Apply the same
                      filter chain as the Telegram icon to push it into
                      the cobalt-moondust band. */}
                  <div
                    className="size-3.5 opacity-55 transition-opacity group-hover:opacity-90"
                    style={{
                      filter:
                        "brightness(0) saturate(100%) invert(72%) sepia(20%) saturate(900%) hue-rotate(195deg) brightness(0.95)",
                    }}
                  >
                    <ParagraphIcon />
                  </div>
                </a>
                <a
                  href="https://discord.gg/thehoneyjar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:flex group items-center justify-center rounded p-1"
                >
                  <svg
                    className="size-3.5 opacity-55 transition-opacity group-hover:opacity-90"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    style={{ color: "rgba(155, 176, 240, 1)" }}
                  >
                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
                  </svg>
                </a>
                <div className="hidden md:block mx-1 h-4 w-px" style={{ background: "rgba(62, 97, 250, 0.22)" }}></div>
                {Object.values(wallets).some((wallet) => wallet) && (
                  <div className="hidden md:flex items-center">
                    <div className="mx-1 h-4 w-px" style={{ background: "rgba(62, 97, 250, 0.22)" }}></div>

                    {/* HENLO Balance — recessed cobalt-rim chrome
                        (was amber-bordered industrial). Matches the
                        AFTER HOURS register: dark inset, cobalt rim,
                        cobalt-tinted moondust text. Token glyphs keep
                        their brand color (they're brand assets, not
                        chrome). */}
                    <div
                      className="group flex h-7 items-center gap-1.5 px-2.5 text-xs"
                      style={{
                        background:
                          "linear-gradient(135deg, #08080a 0%, #11111a 100%)",
                        boxShadow: `
                          inset 0 2px 6px rgba(0, 0, 0, 0.7),
                          inset 0 -1px 0 rgba(62, 97, 250, 0.18),
                          0 1px 0 rgba(255, 255, 255, 0.03)
                        `.trim(),
                        border: "1px solid rgba(62, 97, 250, 0.22)",
                      }}
                    >
                      <Image
                        src="/token/henlo.webp"
                        alt="HENLO"
                        width={14}
                        height={14}
                        className="rounded-full"
                        style={{
                          filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4))",
                        }}
                      />
                      <span
                        className="font-pressStart2P text-[7px] tracking-[0.05em]"
                        style={{
                          color: "rgba(155, 176, 240, 0.85)",
                          textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)",
                        }}
                      >
                        {fmtToken(henloAmount)}
                      </span>
                      <Image
                        src="/token/bera.png"
                        alt="BERA"
                        width={14}
                        height={14}
                        className="rounded-full"
                        style={{
                          filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4))",
                        }}
                      />
                      <span
                        className="font-pressStart2P text-[7px] tracking-[0.05em]"
                        style={{
                          color: "rgba(155, 176, 240, 0.85)",
                          textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)",
                        }}
                      >
                        {fmtToken(beraAmount)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Auth Section - Login only when not signed in.
                    Recessed cobalt-rim screen — same TICKER_CONTAINER
                    vocabulary as the AFTER HOURS screen so it reads as
                    part of the same chrome run. Sized to match the
                    neighboring HENLO/BERA balance pill (h-7) and font
                    scaled to PressStart2P 7px so it doesn't tower over
                    the rest of the sub-nav. */}
                {!Object.values(wallets).some((wallet) => wallet) && (
                  <button
                    className="navbar-login-kansei group relative flex h-7 items-center justify-center overflow-hidden rounded-[3px] px-4 font-pressStart2P text-[7px] uppercase tracking-[0.22em]"
                    onClick={() => login()}
                    style={{
                      ...ARCADE_STYLES.TICKER_CONTAINER,
                      transition: `${KANSEI_DEAD.TRANSITION.BORDER_QUANTUM}, ${KANSEI_DEAD.TRANSITION.SHADOW_QUANTUM}`,
                    }}
                  >
                    <TVOverlay intensity="light" />
                    <span
                      className="kansei-flicker-living-c relative z-10"
                      style={{
                        color: "rgba(155, 176, 240, 0.85)",
                        textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)",
                      }}
                    >
                      Login
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="pointer-events-none relative z-20 mx-auto flex h-full max-w-[112rem] items-end justify-between px-6 pb-10">
            <div className="flex items-end gap-6">
              <Link
                href="/"
                rel="noopener noreferrer"
                className="pointer-events-auto relative -mb-12 flex size-40 shrink-0 items-center justify-center"
              >
                <Image
                  src="/henlo_community_big.webp"
                  alt="henlo_logo"
                  fill
                  className="kansei-falling-sign object-contain"
                  style={{
                    // Hibernation: dead + actively falling off the wall.
                    // The kansei-falling-sign keyframe drives a slow
                    // gravity slip — drifts toward steeper tilt, takes a
                    // sudden small slip (steps(1) hard drop), barely
                    // catches itself, slowly recovers. 28s cycle so the
                    // motion reads as gravity, not animation.
                    filter: HIBERNATION_POKU_FILTER_STRONG,
                    opacity: 0.85,
                  }}
                />
              </Link>
            </div>
            {/* AFTER HOURS nav — individual apps fanned out as direct
                character links. Each app wears its associated bear
                from the old dropdown. Locked items render dimmer +
                non-clickable; mememaker + incinerator stay clickable. */}
            <div className="pointer-events-none -mb-3 mr-20 flex items-end justify-center gap-3">
              {navigationItems.map((item, index) => {
                const flicker = ["a", "b", "c"][index % 3];
                // KANSEI register routing: live items run the "living"
                // flicker (lit baseline, brief struggle, hard recovery);
                // locked items run the "dying" flicker (dim baseline,
                // rare gasp toward life). Operator (session-4): "alive
                // ones but barely should be lit fully then barely;
                // others are dead but trying to breathe."
                const flickerClass = item.isLocked
                  ? `kansei-flicker-${flicker}`
                  : `kansei-flicker-living-${flicker}`;
                const inner = (
                  <>
                    {/* Character — fanned out at size-14. KANSEI flicker
                        is the only animation; transform on hover handled
                        directly. Locked items get the strong dead filter
                        + lower opacity so the silhouette reads as fully
                        wound down. */}
                    <div
                      className="absolute -top-8 size-14"
                      style={{
                        transform: "translateZ(-4px)",
                        transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      <Image
                        src={item.bearImage}
                        alt=""
                        fill
                        className={`object-cover ${flickerClass}`}
                        style={{
                          // Filter routed by isLocked: live chars get the
                          // LIVING tier (mostly colored, fighting to stay
                          // alive); locked chars get the STRONG tier
                          // (greyscale, wound down).
                          filter: item.isLocked
                            ? HIBERNATION_POKU_FILTER_STRONG
                            : HIBERNATION_POKU_FILTER_LIVING,
                          opacity: item.isLocked ? 0.65 : 1,
                        }}
                      />
                    </div>
                    {/* Label — cobalt-tinted moondust (was BRAND yellow).
                        Routes flicker by isLocked: live items get LIVING
                        register flicker (lit baseline, brief struggle),
                        locked items get DYING register flicker (dim
                        baseline, rare gasp). Matches the character
                        image flicker register above. */}
                    <span
                      className={`relative z-10 whitespace-nowrap font-bold ${flickerClass}`}
                      style={{
                        color: "rgba(155, 176, 240, 0.9)",
                        opacity: item.isLocked ? 0.65 : 1,
                        transform: "translateZ(8px)",
                        transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                        textShadow:
                          "0 1px 0 rgba(0, 0, 0, 0.7), " + KANSEI_DEAD.GLOW.WHISPER,
                      }}
                    >
                      {item.title}
                    </span>
                  </>
                );

                const sharedClass =
                  "group pointer-events-auto relative hidden h-12 w-[110px] items-end justify-center px-2 pb-3 font-clash text-[11px] uppercase lg:flex";
                const sharedStyle: React.CSSProperties = {
                  transform: "perspective(400px) rotateX(0deg) translateY(0px) translateZ(0px)",
                  transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                  transformStyle: "preserve-3d",
                };
                const onEnter = (e: React.MouseEvent<HTMLElement>) => {
                  if (item.isLocked) return;
                  e.currentTarget.style.transform =
                    "perspective(400px) rotateX(-8deg) translateY(-3px) translateZ(12px)";
                };
                const onLeave = (e: React.MouseEvent<HTMLElement>) => {
                  e.currentTarget.style.transform =
                    "perspective(400px) rotateX(0deg) translateY(0px) translateZ(0px)";
                };

                return item.isLocked ? (
                  <div
                    key={item.id}
                    aria-disabled
                    className={`${sharedClass} cursor-default`}
                    style={sharedStyle}
                    onMouseEnter={onEnter}
                    onMouseLeave={onLeave}
                  >
                    {inner}
                  </div>
                ) : (
                  <Link
                    key={item.id}
                    href={item.path}
                    className={sharedClass}
                    style={sharedStyle}
                    onMouseEnter={onEnter}
                    onMouseLeave={onLeave}
                  >
                    {inner}
                  </Link>
                );
              })}

              {/* Mobile menu toggle lives in top subnav; menu content below */}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Nav Menu */}
      <Dialog.Root open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/80 md:hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content
            className="fixed inset-0 top-0 z-50 md:hidden h-full overflow-y-auto"
            style={{
              background: "linear-gradient(145deg, #2a2a32 0%, #1a1a22 100%)",
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.5),
                inset 0 1px 1px rgba(255, 255, 255, 0.1),
                inset 0 -1px 1px rgba(0, 0, 0, 0.5)
              `.trim(),
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            {/* Top bar with close */}
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-end gap-2">
                <Image src="/henlo_community_big.webp" alt="henlo" width={28} height={28} />
                <span
                  className="font-clash text-sm font-bold uppercase tracking-wider"
                  style={{
                    color: BRAND_COLORS.yellow,
                    textShadow: `
                      1px 0 0 #3E61FA,
                      -1px 0 0 #3E61FA,
                      0 1px 0 #3E61FA,
                      0 -1px 0 #3E61FA
                    `.trim(),
                  }}
                >
                  Menu
                </span>
              </div>
              <Dialog.Close
                className="rounded-sm opacity-90 ring-offset-background transition-[transform,opacity] hover:scale-110 hover:opacity-100 focus:outline-none"
                aria-label="Close menu"
              >
                <X className="size-5 text-white" />
              </Dialog.Close>
            </div>

            {/* Divider */}
            <div className="h-px w-full bg-white/10" />

            {/* Navigation categories */}
            <div className="px-3 py-3 space-y-4">
              {navigationCategories.map((category) => (
                <div key={category.id} className="space-y-2">
                  <div className="flex items-center gap-2 px-1">
                    <div className="relative size-6">
                      <Image src={category.bearImage} alt="" width={24} height={24} className="object-contain" />
                    </div>
                    <span
                      className="font-clash text-xs font-bold uppercase tracking-wider"
                      style={{
                        color: BRAND_COLORS.yellow,
                        textShadow: `
                          1px 0 0 #3E61FA,
                          -1px 0 0 #3E61FA,
                          0 1px 0 #3E61FA,
                          0 -1px 0 #3E61FA,
                          1px 1px 0 #3E61FA,
                          -1px -1px 0 #3E61FA
                        `.trim(),
                      }}
                    >
                      {category.title}
                    </span>
                  </div>

                  {/* Items list (reuse dropdown item styling) */}
                  <div className="space-y-1.5">
                    {category.items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          handleItemClick(item.path, item.isLocked);
                          setMobileMenuOpen(false);
                        }}
                        disabled={item.isLocked}
                        className="group w-full text-left disabled:opacity-60"
                        style={{ backgroundColor: "transparent" }}
                      >
                        <div
                          className="relative flex min-h-[84px] w-full items-center overflow-hidden rounded-md px-4 py-4 transition-[transform,box-shadow] duration-200 ease-out"
                          style={{
                            background: "linear-gradient(135deg, #0a0a0b 0%, #141416 100%)",
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
                            transform: "scale(1)",
                          }}
                        >
                          {/* TV scanline overlay */}
                          <div
                            className="pointer-events-none absolute inset-0 z-0 opacity-30"
                            style={{
                              background: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 255, 255, 0.02) 2px, rgba(255, 255, 255, 0.02) 4px)`,
                            }}
                          />
                          {/* Background character image (environmental) */}
                          <Image
                            src={getItemCharacter(item.id, category.bearImage)}
                            alt=""
                            width={128}
                            height={128}
                            aria-hidden
                            className={`pointer-events-none absolute -right-2 top-1/2 -translate-y-1/2 select-none ${
                              item.isLocked ? "opacity-5 grayscale" : "opacity-20"
                            }`}
                          />
                          {/* Foreground text */}
                          <div className="relative z-10 flex w-full items-center justify-between gap-3">
                            <div className="flex min-w-0 flex-col">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="truncate font-clash text-sm font-semibold uppercase leading-tight text-white/90 transition-colors duration-200 group-hover:text-white">
                                  {item.title}
                                </span>
                              </div>
                              {item.subtitle && (
                                <span className="mt-2 line-clamp-1 truncate font-pressStart2P text-[7px] uppercase tracking-widest text-white/60">
                                  {item.subtitle}
                                </span>
                              )}
                            </div>
                            {item.isLocked ? (
                              <Lock className="ml-2 size-4 shrink-0 text-muted-foreground" />
                            ) : null}
                          </div>

                          {item.isLocked ? (
                            <div className="absolute inset-0 z-0 rounded-md bg-black/10" />
                          ) : null}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {/* Divider */}
              <div className="h-px w-full bg-white/10" />

              {/* Links section moved from top bar for mobile - condensed row */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <span
                    className="font-clash text-xs font-bold uppercase tracking-wider"
                    style={{
                      color: BRAND_COLORS.yellow,
                      textShadow: `
                        1px 0 0 #3E61FA,
                        -1px 0 0 #3E61FA,
                        0 1px 0 #3E61FA,
                        0 -1px 0 #3E61FA
                      `.trim(),
                    }}
                  >
                    Links
                  </span>
                </div>

                <div className="px-1 pb-2 flex flex-wrap items-center gap-2">
                  {/* Socials */}
                  <div className="flex items-center gap-2">
                    <a
                      href="https://t.me/henlotelegram"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-center rounded p-1"
                    >
                      <Image
                        src="/social/telegram.svg"
                        alt="telegram"
                        width={14}
                        height={14}
                        className="size-3.5 opacity-60 transition-opacity group-hover:opacity-100"
                        style={{
                          filter: "brightness(0) saturate(100%) invert(90%)",
                        }}
                      />
                      <span className="sr-only">Telegram</span>
                    </a>
                    <a
                      href="https://paragraph.xyz/@henlo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-center rounded p-1"
                    >
                      <div className="size-3.5 text-muted-foreground transition-colors group-hover:text-white">
                        <ParagraphIcon />
                      </div>
                      <span className="sr-only">Paragraph</span>
                    </a>
                    <a
                      href="https://discord.gg/thehoneyjar"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-center rounded p-1"
                    >
                      <svg
                        className="size-3.5 opacity-60 transition-opacity group-hover:opacity-100"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        style={{ color: "rgba(255, 255, 255, 0.9)" }}
                      >
                        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
                      </svg>
                      <span className="sr-only">Discord</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Profile Drawer */}
      <Dialog.Root open={profileDrawerOpen} onOpenChange={setProfileDrawerOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

          <Dialog.Content
            className="fixed right-0 top-0 z-50 h-full w-[26rem] data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
            style={{
              // Hibernation chassis — echoes navbar TV_FRAME. The bright
              // cobalt slab read as a live banner; the room is AFTER HOURS,
              // so the dialog's outer material is moondust, not daylight.
              background:
                "linear-gradient(145deg, #2a2a32 0%, #1f1f25 30%, #161620 70%, #0d0d12 100%)",
              boxShadow: `
                -8px 0 32px rgba(0, 0, 0, 0.5),
                -4px 0 16px rgba(0, 0, 0, 0.4),
                inset 1px 0 0 rgba(62, 97, 250, 0.18),
                inset 0 1px 0 rgba(62, 97, 250, 0.14)
              `.trim(),
              borderLeft: "1px solid rgba(62, 97, 250, 0.22)",
            }}
          >
            {/* CRT scanline residue — KANSEI_DEAD register. Same powered-
                down CRT reading as the navbar chassis; the drawer is part
                of the same hibernating room. Sits behind the user's
                selected header image so the LIVING band on top still
                broadcasts the user's own register. */}
            <div
              className="pointer-events-none absolute inset-0 z-0"
              style={{
                backgroundImage: KANSEI_DEAD.SCANLINE_BG_2,
                opacity: 0.6,
                mixBlendMode: "screen",
              }}
            />
            {/* Top header strip — cosmic-carpet desktop atmosphere
                ported from CarpetBackground.tsx. Drifting Pokus + far
                mandorla on a starfield, with the cosmic accent overlay
                stack (top starlight wash + bottom nostalgia fade +
                radial vignette). The drawer's top reads as a window
                onto the same theater the lobby is in. */}
            <div
              className="absolute inset-x-0 top-0 h-40 w-full overflow-hidden"
              style={{
                background: "#050507",
                boxShadow: `
                  inset 0 1px 0 rgba(255, 255, 255, 0.04),
                  inset 0 -1px 0 rgba(0, 0, 0, 0.55)
                `.trim(),
              }}
            >
              {/* Cosmic carpet — drifting Pokus on starfield */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  backgroundImage: "url('/lobby/backdrop/cosmic-carpet.png')",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center top",
                  backgroundSize: "cover",
                  opacity: 0.55,
                }}
              />
              {/* Cosmic accent — top starlight wash + bottom nostalgia
                  fade + radial vignette. Same triple-stack as the lobby
                  CarpetBackground so the drawer's window reads as the
                  same theater. */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background: `
                    radial-gradient(120% 70% at 50% 0%, rgba(62, 97, 250, 0.10) 0%, transparent 55%),
                    radial-gradient(140% 100% at 50% 100%, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0) 60%),
                    radial-gradient(circle at center, transparent 35%, rgba(0,0,0,0.55) 100%)
                  `,
                }}
              />
              {/* CRT scanline residue — KANSEI_DEAD register */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  backgroundImage: KANSEI_DEAD.SCANLINE_BG_2,
                  opacity: 0.6,
                  mixBlendMode: "screen",
                }}
              />
            </div>
            {/* Inner bezel frame - darkening effect */}
            <div
              className="pointer-events-none absolute left-0 right-0 top-0"
              style={{
                height: "120px",
                background:
                  "linear-gradient(145deg, rgba(0, 0, 0, 0.2) 0%, transparent 50%, rgba(255, 255, 255, 0.03) 100%)",
                boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.5)",
              }}
            />

            {/* Blue background gradient overlay - reduced height */}
            <div
              className="pointer-events-none absolute left-0 right-0 top-0"
              style={{
                height: "120px",
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, transparent 40%)",
                opacity: 0.8,
              }}
            />

            {/* Removed lock texture overlay */}

            {/* Logout + Close buttons. AFTER HOURS register — in a
                closed room you don't manage settings; you leave. The
                Edit-profile flow still auto-opens for first-time users
                via the useEffect at the top of Navbar; the dropdown
                here was redundant surface area. */}
            <div className="absolute right-4 top-3 z-40 flex items-center gap-2">
              <button
                onClick={() => {
                  logout();
                  setProfileDrawerOpen(false);
                }}
                className="group relative p-2 transition-transform duration-200 hover:scale-105 active:scale-95 focus:outline-none"
                style={{
                  background: "linear-gradient(135deg, #1f1f25 0%, #161620 100%)",
                  border: "1px solid rgba(62, 97, 250, 0.22)",
                  borderRadius: "6px",
                  boxShadow: `
                    0 2px 4px rgba(0, 0, 0, 0.4),
                    inset 0 1px 0 rgba(255, 255, 255, 0.04),
                    inset 0 -1px 0 rgba(62, 97, 250, 0.18)
                  `
                    .replace(/\s+/g, " ")
                    .trim(),
                }}
                aria-label="Log out"
                title="Log out"
              >
                <LogOut className="size-4 text-white/60 transition-colors duration-200 group-hover:text-white" />
              </button>
              <Dialog.Close
                className="group relative p-2 transition-transform duration-200 hover:scale-105 active:scale-95 focus:outline-none"
                style={{
                  // Settings/Close buttons — echo navbar button-scale
                  // chrome (cobalt rim institutional), matching the same
                  // material as the soundtrack panel buttons. The drawer's
                  // top-right cluster reads as the same room's controls.
                  background: "linear-gradient(135deg, #1f1f25 0%, #161620 100%)",
                  border: "1px solid rgba(62, 97, 250, 0.22)",
                  borderRadius: "6px",
                  boxShadow: `
                    0 2px 4px rgba(0, 0, 0, 0.4),
                    inset 0 1px 0 rgba(255, 255, 255, 0.04),
                    inset 0 -1px 0 rgba(62, 97, 250, 0.18)
                  `
                    .replace(/\s+/g, " ")
                    .trim(),
                }}
              >
                <X className="size-4 text-white/60 transition-colors duration-200 group-hover:text-white" />
                <span className="sr-only">Close</span>
              </Dialog.Close>
            </div>

            {/* Large Profile Picture now lives inside the grey panel top-left */}

            {/* Username floating on TOP of everything, straddling the bezel */}
            <div
              className={`absolute left-4 z-30 transform transition-all duration-700 ease-out ${
                profileDrawerOpen ? "translate-x-0 opacity-100 scale-100" : "-translate-x-20 opacity-0 scale-95"
              }`}
              style={{ top: "116px" }}
            >
              <div className="flex flex-col items-start gap-1">
                <h2
                  className="font-clash text-3xl font-black uppercase tracking-wider kansei-flicker-living-b"
                  style={{
                    // Cobalt-tinted moondust — same migration as the
                    // MEME MAKER nav labels ("was BRAND yellow"). Yellow
                    // now reserved for ONE slot only: the BYE MOONDUST
                    // CTA. Username reads as institutional name plate
                    // in the LIVING data register, matching the HENLO
                    // balance row + navbar nav labels.
                    color: "rgba(155, 176, 240, 0.9)",
                    textShadow: `0 1px 0 rgba(0, 0, 0, 0.7), ${KANSEI_DEAD.GLOW.WHISPER}`,
                  }}
                >
                  {displayName}
                </h2>
                {displayAddress && (
                  <div className="font-pressStart2P text-[10px] text-white/70 -mt-1 tracking-wide">
                    {displayAddress}
                  </div>
                )}
                {/* Settings and log out available via top-right settings button */}
              </div>
              {/* Username inline editing removed in favor of dialog */}
            </div>

            {/* Character avatar — moved out of the grey panel's stacking
                context so the panel (z-20) renders ON TOP of the avatar's
                lower body + drop-shadow. The visible portion peeks up
                onto the cosmic-carpet header; the rest tucks behind the
                panel edge. LIVING register matches the music-player
                album-art treatment. */}
            <div className="absolute -top-2 left-4 z-[15]">
              <div
                className="relative size-32 cursor-pointer drop-shadow-xl"
                title="Edit avatar"
              >
                <Image
                  src={character || DEFAULT_PROFILE_CHARACTER}
                  alt="char"
                  fill
                  className="object-contain kansei-flicker-living-c"
                  style={{ filter: HIBERNATION_POKU_FILTER_LIVING }}
                />
              </div>
            </div>

            {/* Grey Panel Container - panel renders ON TOP of avatar's
                lower body via higher z-index */}
            <div
              className="absolute bottom-0 left-0 right-0 z-20"
              style={{ height: "calc(100% - 120px)" }}
            >
              {/* Removed peeking character; avatar sits at panel top-left */}

              {/* Grey Panel - ABOVE the character (higher z-index) */}
              <div
                className="relative z-10 h-full w-full"
                style={{
                  background:
                    "linear-gradient(145deg, #3a3a42 0%, #2a2a32 30%, #1a1a22 70%, #0a0a12 100%)",
                  boxShadow: `
                    0 -6px 16px rgba(0, 0, 0, 0.6),
                    0 4px 12px rgba(0, 0, 0, 0.6),
                    inset 0 2px 2px rgba(255, 255, 255, 0.15),
                    inset 0 -2px 2px rgba(0, 0, 0, 0.6),
                    0 0 0 1px rgba(255, 255, 255, 0.1)
                  `.trim(),
                  borderTop: "3px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                {/* No extra background here; the blue header above already displays it */}
                {/* Content area with padding */}
                <div className="relative flex h-full min-h-0 flex-col">
                  {/* Stats Section - moved padding to right only */}
                  <div className="flex h-full min-h-0 flex-col px-3 pt-20">
                    {/* Portfolio Value Display — TICKER_CONTAINER chrome.
                        Echoes the navbar soundtrack panel + music-player
                        deck so the drawer reads as the same room's PA
                        system, not a generic black recess. */}
                    <div className="mb-2">
                      <div
                        className="relative overflow-hidden rounded-lg"
                        style={{
                          ...ARCADE_STYLES.TICKER_CONTAINER,
                          backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, linear-gradient(135deg, #08080a 0%, #11111a 100%)`,
                        }}
                      >
                        {/* Content Container with padding for TV bezel */}
                        <div className="relative z-30 flex flex-col p-4">
                          {/* Portfolio Value - white text */}
                          <div
                            className="mb-1 font-pressStart2P text-[24px]"
                            style={{
                              color: "#ffffff",
                              textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                              letterSpacing: "0.02em",
                              opacity: 0.95,
                            }}
                          >
                            {fmtUSDWhole(netValueUSD)}
                          </div>

                          {/* HENLO Balance Display */}
                          <div
                            className="mb-3 flex items-center justify-between"
                            style={{
                              padding: "4px 0",
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <Image
                                src="/token/henlo.webp"
                                alt="HENLO"
                                width={16}
                                height={16}
                                className="rounded-full"
                                style={{
                                  filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))",
                                }}
                              />
                              <span
                                className="font-pressStart2P text-[10px] uppercase kansei-flicker-living-c"
                                style={{
                                  // Data, not identity — moondust register
                                  // matches navbar track info + music-
                                  // player. Yellow stays reserved for the
                                  // two signal slots that earn it (the
                                  // username beacon and the BYE MOONDUST
                                  // farewell CTA).
                                  color: "rgba(200, 215, 255, 0.95)",
                                  textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                {fmtToken(henloAmount)} HENLO{" "}
                                {henloAmount > 0 && priceData?.price ? (
                                  <span className="text-muted-foreground">
                                    ($
                                    {(henloAmount * priceData.price).toFixed(2)})
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground"></span>
                                )}
                              </span>
                            </div>
                            {henloAmount === 0 && (
                              <span className="font-pressStart2P text-[6px] text-muted-foreground uppercase">
                                NO TOKENS
                              </span>
                            )}
                          </div>

                          {/* Conditional Button Display based on HENLO holdings */}
                          {henloAmount > 0 ? (
                            // User has HENLO - show burn and build buttons
                            <div className="grid grid-cols-2 gap-2">
                              {/* Burn Button - Gray with character */}
                              <div className="transition-transform will-change-transform hover:scale-[1.01] active:scale-[0.99]">
                                <Link
                                  href="/incinerator"
                                  className="relative block h-11 w-full overflow-hidden rounded-[7px] font-pressStart2P text-[10px] uppercase text-white transition-[filter] duration-200 hover:brightness-110"
                                  style={{
                                    // Hibernation institutional button —
                                    // echoes ARCADE_STYLES.SECTION_BODY at
                                    // button scale. Cobalt rim instead of
                                    // generic black border so the button
                                    // belongs to the same room.
                                    background: "linear-gradient(135deg, #1f1f25 0%, #161620 100%)",
                                    boxShadow: `
                                      0 2px 4px rgba(0, 0, 0, 0.35),
                                      inset 0 1px 1px rgba(255, 255, 255, 0.04),
                                      inset 0 -1px 0 rgba(62, 97, 250, 0.18)
                                    `.trim(),
                                    border: "1px solid rgba(62, 97, 250, 0.18)",
                                  }}
                                >
                                  {/* Incinerator character - larger */}
                                  <Image
                                    src="/poku/incinerator.png"
                                    alt=""
                                    width={64}
                                    height={64}
                                    className="absolute -left-5 top-1/2 z-20 -translate-y-1/2"
                                    style={{
                                      filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.5))",
                                    }}
                                  />

                                  <span
                                    className="relative z-10 flex h-full items-center justify-center pl-12"
                                    style={{
                                      textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                    }}
                                  >
                                    <span>Burn</span>
                                  </span>
                                </Link>
                              </div>

                              {/* Build Button - Gray with character */}
                              <div className="transition-transform will-change-transform hover:scale-[1.01] active:scale-[0.99]">
                                <Link
                                  href="/wall"
                                  className="relative block h-11 w-full overflow-hidden rounded-[7px] font-pressStart2P text-[10px] uppercase text-white transition-[filter] duration-200 hover:brightness-110"
                                  style={{
                                    background: "linear-gradient(135deg, #525252 0%, #404040 100%)",
                                    boxShadow: `
                                      0 2px 4px rgba(0,0,0,0.2),
                                      inset 0 1px 1px rgba(255,255,255,0.1)
                                    `.trim(),
                                    border: "1px solid rgba(0,0,0,0.2)",
                                  }}
                                >
                                  {/* Trump character - larger */}
                                  <Image
                                    src="/poku/trump.png"
                                    alt=""
                                    width={64}
                                    height={64}
                                    className="absolute -left-5 top-1/2 z-20 -translate-y-1/2"
                                    style={{
                                      filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.5))",
                                    }}
                                  />

                                  <span
                                    className="relative z-10 flex h-full items-center justify-center pl-12"
                                    style={{
                                      textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                    }}
                                  >
                                    <span>Build</span>
                                  </span>
                                </Link>
                              </div>
                            </div>
                          ) : (
                            // User has no HENLO — AFTER HOURS farewell
                            // CTA. "There is no henlo only gudbye" — the
                            // button waves out instead of inviting in.
                            // Hibernation chrome with yellow text reads as
                            // an exit sign at night, not a daylight invite.
                            <div className="w-full">
                              <div className="relative transition-transform will-change-transform hover:scale-[1.01] active:scale-[0.99]">
                                <Link
                                  href="https://opensea.io/token/bera_chain/0xb2f776e9c1c926c4b2e54182fac058da9af0b6a5"
                                  target="_blank"
                                  className="relative block h-11 w-full rounded-[7px] font-pressStart2P text-[10px] uppercase transition-[filter] duration-200 hover:brightness-110"
                                  style={{
                                    background: "linear-gradient(135deg, #1f1f25 0%, #161620 100%)",
                                    boxShadow: `
                                      0 2px 4px rgba(0, 0, 0, 0.4),
                                      inset 0 1px 0 rgba(255, 255, 255, 0.04),
                                      inset 0 -1px 0 rgba(62, 97, 250, 0.18)
                                    `.trim(),
                                    border: "1px solid rgba(62, 97, 250, 0.22)",
                                    // Cobalt-tinted moondust — same FEEL
                                    // as username + MEME MAKER + HENLO
                                    // balance. The bye/buy irony lives
                                    // in the WORDS; yellow was the
                                    // megaphone, now silent. AFTER HOURS
                                    // smirks, doesn't shout.
                                    color: "rgba(155, 176, 240, 0.9)",
                                  }}
                                >
                                  {/* Astronaut Poku — the room's mascot
                                      waves the user out. LIVING register
                                      (alive within hibernation) matches
                                      the navbar marquee astronaut + the
                                      music-player album art. Extends past
                                      the button edge — no overflow-hidden
                                      so the character can breathe. */}
                                  <Image
                                    src="/poku/astronaut.png"
                                    alt=""
                                    width={64}
                                    height={64}
                                    className="absolute -left-5 top-1/2 z-20 -translate-y-1/2"
                                    style={{
                                      filter: HIBERNATION_POKU_FILTER_LIVING,
                                    }}
                                  />

                                  {/* Bye text — LIVING register flicker
                                      matches the username, the astronaut
                                      poku, and the music-player album
                                      art. Same breath, same room. */}
                                  <span
                                    className="kansei-flicker-living-c relative z-10 flex h-full items-center justify-center"
                                    style={{
                                      textShadow: `0 1px 0 rgba(0, 0, 0, 0.7), ${KANSEI_DEAD.GLOW.WHISPER}`,
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    <span>BYE MOONDUST</span>
                                  </span>
                                </Link>
                              </div>

                              {/* Arcade subtitle — AFTER HOURS register */}
                              <div className="mt-2 text-center font-pressStart2P text-[6px] uppercase text-muted-foreground">
                                ONE LAST WAVE
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Tabbed Content Panel */}
                    <Tabs
                      value={profileTab}
                      onValueChange={setProfileTab}
                      className="flex flex-1 min-h-0 flex-col"
                    >
                      {/* Tab Navigation */}
                      <TabsList variant="profile" className="w-full">
                        <TabsTrigger variant="profile" value="tokens">
                          Tokens
                        </TabsTrigger>
                        <TabsTrigger variant="profile" value="nfts">
                          NFTs
                        </TabsTrigger>
                        <TabsTrigger variant="profile" value="activity">
                          Activity
                        </TabsTrigger>
                      </TabsList>

                      {/* Content Panel — same TICKER_CONTAINER recess as
                          the Portfolio Value above so the tabbed surface
                          reads as a continuation of the same instrument,
                          not a separate panel. */}
                      <div
                        className="relative flex flex-col flex-1 overflow-hidden min-h-0"
                        style={{
                          backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, linear-gradient(135deg, #08080a 0%, #11111a 100%)`,
                          boxShadow: `
                            inset 0 2px 10px rgba(0, 0, 0, 0.7),
                            inset 0 -1px 0 rgba(62, 97, 250, 0.18)
                          `.trim(),
                          borderLeft: "1px solid rgba(62, 97, 250, 0.18)",
                          borderRight: "1px solid rgba(62, 97, 250, 0.18)",
                          borderBottom: "1px solid rgba(62, 97, 250, 0.18)",
                        }}
                      >
                        {/* Tab Content */}
                        <TabsContent
                          value="tokens"
                          className="relative z-10 mt-0 flex-1 overflow-hidden min-h-0"
                        >
                          <ScrollArea type="auto" className="h-full pr-1">
                            <div className="space-y-2.5 px-3 py-3">
                              {/* Unified holdings list from hook: sorted, includes zero balances */}
                              {sortedHoldings.map((t) => (
                                <div
                                  key={t.key}
                                  className={`flex items-center justify-between py-1.5 ${
                                    t.amount <= 0 ? "opacity-50 grayscale" : ""
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Image
                                      src={t.icon}
                                      alt={t.label}
                                      width={40}
                                      height={40}
                                      className="rounded-full"
                                    />
                                    <div>
                                      <div className="font-pressStart2P text-[10px] uppercase text-white whitespace-nowrap">
                                        {t.label}
                                      </div>
                                      <div className="font-pressStart2P text-[9px] text-white/70 whitespace-nowrap">
                                        {fmtToken(t.amount)} {t.unit}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-pressStart2P text-[11px] text-white whitespace-nowrap">
                                      {fmtUSD2(t.usd)}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </TabsContent>

                        {/* NFTs Tab Content */}
                        <TabsContent value="nfts" className="relative z-10 mt-0">
                          <div className="flex items-center justify-center p-8 text-white/40">
                            <p className="font-pressStart2P text-[8px] uppercase">Coming Soon</p>
                          </div>
                        </TabsContent>

                        {/* Activity Tab Content */}
                        <TabsContent value="activity" className="relative z-10 mt-0">
                          <div className="flex items-center justify-center p-8 text-white/40">
                            <p className="font-pressStart2P text-[8px] uppercase">Coming Soon</p>
                          </div>
                        </TabsContent>
                      </div>
                    </Tabs>
                  </div>
                </div>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      {/* Profile setup dialog */}
      <ProfileSetupDialog open={profileSetupOpen} onOpenChange={setProfileSetupOpen} />
    </div>
  );
};

export default Navbar;

// Mount the profile setup dialog near the root
// Note: We export default above; Next will render this component.

// Animation Keyframes
if (typeof window !== "undefined") {
  const existingStyle = document.getElementById("navbar-animations");
  if (!existingStyle) {
    const style = document.createElement("style");
    style.id = "navbar-animations";
    style.textContent = `
      @keyframes phosphorGlow {
        0%, 100% { opacity: 0.02; }
        50% { opacity: 0.03; }
      }

      @keyframes equalizerBar {
        0%, 100% {
          transform: scaleY(0.2);
        }
        25% {
          transform: scaleY(0.6);
        }
        50% {
          transform: scaleY(1);
        }
        75% {
          transform: scaleY(0.4);
        }
      }

      @keyframes marqueeScroll {
        0% {
          transform: translateX(0);
        }
        100% {
          transform: translateX(-50%);
        }
      }

      .navbar-login-kansei:hover {
        box-shadow:
          inset 0 2px 10px rgba(0, 0, 0, 0.8),
          inset 0 -1px 0 rgba(62, 97, 250, 0.35),
          0 1px 0 rgba(255, 255, 255, 0.04),
          0 0 12px rgba(62, 97, 250, 0.18) !important;
      }
    `;
    document.head.appendChild(style);
  }
}
